import React, { useEffect, useState } from "react";
import SwitchIcon from "src/assets/images/switch.svg";
import BookIcon from "src/assets/images/book.svg";
import { useAppDispatch } from "src/hooks/redux";
import { objectiveConfirmed } from "./action";
import DotLoader from "src/components/reusable/dot-loader";

interface SummaryRow {
  parameter: string;
  value: string;
}

interface Props {
  file?: File;
}

const AutoFilledSummaryTable: React.FC<Props> = ({ file }) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [loading, setLoading] = useState(false);

  const flattenObject = (obj: any, prefix = ""): SummaryRow[] => {
    const flatRows: SummaryRow[] = [];

    for (const key in obj) {
      const value = obj[key];
      const label = prefix ? `${prefix}.${key}` : key;

      if (Array.isArray(value)) {
        flatRows.push({
          parameter: label,
          value: value.join(", "),
        });
      } else if (typeof value === "object" && value !== null) {
        flatRows.push(...flattenObject(value, label));
      } else {
        flatRows.push({
          parameter: label,
          value: String(value),
        });
      }
    }

    return flatRows;
  };

  const uploadFile = async () => {
    if (file) {
      setLoading(true);
      try {
        const response = await dispatch(objectiveConfirmed({ file })).unwrap();
        const confirmationJson = JSON.parse(response.data.confirmation);
        const dynamicRows = flattenObject(confirmationJson);
        setRows(dynamicRows);
      } catch (err) {
        console.error("Failed to parse confirmation JSON:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (file) {
      uploadFile();
    }
  }, [file]);

  return (
    <div
      className={`h-screen rounded-lg w-full overflow-hidden transition-all duration-300 ease-in-out ${open
        ? "flex-[0_0_100%] sm:flex-[0_0_75%] lg:flex-[0_0_460px] max-w-full lg:max-w-[460px]"
        : "flex-[0_0_100%] sm:flex-[0_0_215px] max-w-full sm:max-w-[215px]"
        }`}
    >
      <div className="flex flex-col h-full bg-opacity-50">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <img src={BookIcon} alt="Book Icon" className="w-3 h-3 object-contain" />
            <h2 className="text-lg font-semibold">Objective Confirmation & Risk Calibration</h2>
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="cursor-pointer rounded border border-[#DFE3E8] p-1 hover:bg-gray-100"
            aria-label="Toggle Panel"
          >
            <img src={SwitchIcon} alt="Toggle" className="w-3 h-3 object-contain" />
          </button>
        </div>

        {/* Table */}
        <div className="w-full overflow-auto">
          <div className="font-semibold text-base text-[#373D3F] mb-1">Summary</div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500 animate-pulse"><DotLoader /></div>
          ) : (
            <table className="w-full text-xs rounded-md">
              <thead>
                <tr className="text-left border-b border-b-[#E0E0E0]">
                  <th className="bg-white p-1 font-medium text-gray-600 min-[240px]">
                    Parameter
                  </th>
                  <th className="bg-white p-1 font-medium text-gray-600">Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index} className="border-b last:border-0">
                    <td className="p-1 align-top font-medium text-gray-700 whitespace-nowrap">
                      {row.parameter}
                    </td>
                    <td className="p-1 text-gray-800">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoFilledSummaryTable;
